import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import {
  Star,
  Check,
  X,
  RotateCcw,
  LogOut,
  KeyRound,
  RefreshCw,
  Phone as PhoneIcon,
  Mail as MailIcon,
  Car,
  Calendar,
  MapPin,
  DollarSign,
  MessageSquare,
  ClipboardList,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { useToast } from "../hooks/use-toast";
import { businessInfo } from "../mock";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STORAGE_KEY = "hfmk_admin_token";

const Admin = () => {
  const { toast } = useToast();
  const [token, setToken] = useState(() => localStorage.getItem(STORAGE_KEY) || "");
  const [draftToken, setDraftToken] = useState("");
  const [section, setSection] = useState("reviews");

  const handleLogin = (e) => {
    e.preventDefault();
    if (!draftToken.trim()) return;
    localStorage.setItem(STORAGE_KEY, draftToken.trim());
    setToken(draftToken.trim());
    setDraftToken("");
  };

  const handleLogout = () => {
    localStorage.removeItem(STORAGE_KEY);
    setToken("");
  };

  if (!token) {
    return (
      <div className="min-h-screen fire-bg-subtle flex items-center justify-center px-5 py-10">
        <div className="w-full max-w-md brand-card rounded-2xl p-7 border-red-900/40">
          <img src={businessInfo.logoUrl} alt="1/2 Fast Mobile Keys" className="h-12 w-auto" />
          <h1 className="mt-5 text-white font-extrabold text-2xl">Admin Sign-In</h1>
          <p className="mt-2 text-zinc-400 text-sm">
            Enter your password to manage reviews and bookings.
          </p>

          <form onSubmit={handleLogin} className="mt-6 space-y-3">
            <div>
              <label className="text-sm font-semibold text-zinc-200">Password</label>
              <Input
                type="password"
                value={draftToken}
                onChange={(e) => setDraftToken(e.target.value)}
                placeholder="Enter password"
                className="mt-1.5 h-11 bg-zinc-900 border-zinc-700 text-white placeholder:text-zinc-600"
                autoFocus
              />
            </div>
            <Button type="submit" className="w-full bg-brand-red text-white font-semibold h-11">
              <KeyRound className="w-4 h-4 mr-2" />
              Sign In
            </Button>
          </form>

          <p className="mt-5 text-xs text-zinc-500 leading-relaxed">
            Your password is the same secret used in your review approval emails. It's saved on this device after sign-in.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white">
      <header className="border-b border-zinc-900 bg-zinc-950">
        <div className="max-w-5xl mx-auto px-5 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src={businessInfo.logoUrl} alt="" className="h-10 w-auto" />
            <div>
              <div className="text-yellow-400 text-[10px] font-bold uppercase tracking-widest">Admin</div>
              <div className="font-extrabold text-lg leading-none">Dashboard</div>
            </div>
          </div>
          <Button
            variant="outline"
            onClick={handleLogout}
            className="bg-transparent border-zinc-700 text-zinc-200 hover:bg-zinc-900 h-9"
          >
            <LogOut className="w-4 h-4 mr-2" />
            Sign Out
          </Button>
        </div>
        <div className="max-w-5xl mx-auto px-5 pb-3 flex gap-2">
          <SectionTab
            label="Reviews"
            icon={<MessageSquare className="w-4 h-4" />}
            active={section === "reviews"}
            onClick={() => setSection("reviews")}
          />
          <SectionTab
            label="Bookings"
            icon={<ClipboardList className="w-4 h-4" />}
            active={section === "bookings"}
            onClick={() => setSection("bookings")}
          />
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-5 py-8">
        {section === "reviews" ? (
          <ReviewsPanel token={token} toast={toast} onAuthFail={handleLogout} />
        ) : (
          <BookingsPanel token={token} toast={toast} onAuthFail={handleLogout} />
        )}
      </main>
    </div>
  );
};

const SectionTab = ({ label, icon, active, onClick }) => (
  <button
    onClick={onClick}
    className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-semibold transition-colors ${
      active
        ? "bg-red-600 text-white"
        : "bg-zinc-900 text-zinc-400 hover:text-white"
    }`}
  >
    {icon}
    {label}
  </button>
);

/* ---------- REVIEWS ---------- */
const ReviewsPanel = ({ token, toast, onAuthFail }) => {
  const [tab, setTab] = useState("pending");
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busyId, setBusyId] = useState(null);

  const fetchReviews = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(
        `${API}/reviews/all?token=${encodeURIComponent(token)}&status=${tab}`
      );
      setReviews(res.data || []);
    } catch (err) {
      if (err?.response?.status === 403) {
        toast({ title: "Invalid password", description: "Please sign in again." });
        onAuthFail();
      } else {
        toast({ title: "Couldn't load reviews", description: "Try again in a moment." });
      }
    } finally {
      setLoading(false);
    }
  }, [token, tab, toast, onAuthFail]);

  useEffect(() => {
    fetchReviews();
  }, [fetchReviews]);

  const updateStatus = async (id, action) => {
    setBusyId(id);
    try {
      await axios.post(
        `${API}/reviews/${id}/status?action=${action}&token=${encodeURIComponent(token)}`
      );
      toast({
        title:
          action === "approve" ? "Approved" : action === "reject" ? "Rejected" : "Reset to pending",
      });
      setReviews((prev) => prev.filter((r) => r.id !== id));
    } catch (err) {
      toast({ title: "Couldn't update", description: "Try again." });
    } finally {
      setBusyId(null);
    }
  };

  return (
    <>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex gap-1 bg-zinc-900 rounded-lg p-1">
          {["pending", "approved", "rejected"].map((t) => (
            <button
              key={t}
              onClick={() => setTab(t)}
              className={`px-4 py-2 rounded-md text-sm font-semibold capitalize transition-colors ${
                tab === t ? "bg-red-600 text-white" : "text-zinc-400 hover:text-white"
              }`}
            >
              {t}
            </button>
          ))}
        </div>
        <Button
          variant="outline"
          onClick={fetchReviews}
          disabled={loading}
          className="bg-transparent border-zinc-700 text-zinc-200 hover:bg-zinc-900 h-9"
        >
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? "animate-spin" : ""}`} />
          Refresh
        </Button>
      </div>

      <div className="mt-6">
        {loading && reviews.length === 0 ? (
          <div className="text-zinc-500 text-center py-12">Loading...</div>
        ) : reviews.length === 0 ? (
          <div className="text-zinc-500 text-center py-12 border border-dashed border-zinc-800 rounded-xl">
            No {tab} reviews right now.
          </div>
        ) : (
          <div className="space-y-4">
            {reviews.map((r) => (
              <ReviewCard
                key={r.id}
                review={r}
                tab={tab}
                busy={busyId === r.id}
                onAction={(action) => updateStatus(r.id, action)}
              />
            ))}
          </div>
        )}
      </div>
    </>
  );
};

const ReviewCard = ({ review, tab, busy, onAction }) => {
  const date = new Date(review.created_at).toLocaleString();
  return (
    <div className="brand-card rounded-xl p-5 border-red-900/30">
      <div className="flex items-center gap-1">
        {[...Array(review.rating)].map((_, i) => (
          <Star key={i} className="w-4 h-4 fill-yellow-400 text-yellow-400" />
        ))}
        {[...Array(5 - review.rating)].map((_, i) => (
          <Star key={i} className="w-4 h-4 text-zinc-700" />
        ))}
        <span className="ml-2 text-zinc-500 text-xs">{review.rating}/5</span>
      </div>
      <div className="mt-3 text-zinc-200 text-[15px] leading-relaxed whitespace-pre-wrap break-words">
        {review.text}
      </div>
      <div className="mt-4 pt-3 border-t border-zinc-800 flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="font-bold text-white">{review.name}</div>
          <div className="text-xs text-zinc-500 mt-0.5">
            {review.location} · {date}
          </div>
        </div>
        <div className="flex gap-2">
          {tab !== "approved" && (
            <Button
              size="sm"
              disabled={busy}
              onClick={() => onAction("approve")}
              className="bg-green-600 hover:bg-green-700 text-white font-semibold h-9"
            >
              <Check className="w-4 h-4 mr-1.5" />
              Approve
            </Button>
          )}
          {tab !== "rejected" && (
            <Button
              size="sm"
              disabled={busy}
              variant="outline"
              onClick={() => onAction("reject")}
              className="bg-transparent border-zinc-700 text-zinc-200 hover:bg-zinc-800 h-9"
            >
              <X className="w-4 h-4 mr-1.5" />
              Reject
            </Button>
          )}
          {tab !== "pending" && (
            <Button
              size="sm"
              disabled={busy}
              variant="outline"
              onClick={() => onAction("pending")}
              className="bg-transparent border-zinc-700 text-zinc-200 hover:bg-zinc-800 h-9"
            >
              <RotateCcw className="w-4 h-4 mr-1.5" />
              Move to Pending
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

/* ---------- BOOKINGS ---------- */
const BookingsPanel = ({ token, toast, onAuthFail }) => {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState("all"); // all | confirmed | pending

  const fetchBookings = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/bookings?token=${encodeURIComponent(token)}`);
      setBookings(res.data || []);
    } catch (err) {
      if (err?.response?.status === 403) {
        toast({ title: "Invalid password", description: "Please sign in again." });
        onAuthFail();
      } else {
        toast({ title: "Couldn't load bookings", description: "Try again in a moment." });
      }
    } finally {
      setLoading(false);
    }
  }, [token, toast, onAuthFail]);

  useEffect(() => {
    fetchBookings();
  }, [fetchBookings]);

  const filtered = bookings.filter((b) =>
    filter === "all" ? true : b.status === filter
  );

  return (
    <>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex gap-1 bg-zinc-900 rounded-lg p-1">
          {[
            { v: "all", l: "All" },
            { v: "confirmed", l: "Paid" },
            { v: "pending", l: "Unpaid" },
          ].map(({ v, l }) => (
            <button
              key={v}
              onClick={() => setFilter(v)}
              className={`px-4 py-2 rounded-md text-sm font-semibold transition-colors ${
                filter === v ? "bg-red-600 text-white" : "text-zinc-400 hover:text-white"
              }`}
            >
              {l}
            </button>
          ))}
        </div>
        <Button
          variant="outline"
          onClick={fetchBookings}
          disabled={loading}
          className="bg-transparent border-zinc-700 text-zinc-200 hover:bg-zinc-900 h-9"
        >
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? "animate-spin" : ""}`} />
          Refresh
        </Button>
      </div>

      <div className="mt-6">
        {loading && bookings.length === 0 ? (
          <div className="text-zinc-500 text-center py-12">Loading...</div>
        ) : filtered.length === 0 ? (
          <div className="text-zinc-500 text-center py-12 border border-dashed border-zinc-800 rounded-xl">
            No {filter === "all" ? "" : filter + " "}bookings yet.
          </div>
        ) : (
          <div className="space-y-4">
            {filtered.map((b) => (
              <BookingCard key={b.id} booking={b} />
            ))}
          </div>
        )}
      </div>
    </>
  );
};

const BookingCard = ({ booking }) => {
  const date = new Date(booking.created_at).toLocaleString();
  const isPaid = booking.payment_status === "paid";
  const vehicle =
    [booking.vehicle_year, booking.vehicle_make, booking.vehicle_model].filter(Boolean).join(" ") ||
    "—";
  const when =
    [booking.preferred_date, booking.time_window].filter(Boolean).join(" · ") || "—";

  return (
    <div className="brand-card rounded-xl p-5 border-red-900/30">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="font-bold text-white text-lg">{booking.name}</div>
          <div className="text-xs text-zinc-500 mt-0.5">Booked {date}</div>
        </div>
        <span
          className={`px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider ${
            isPaid
              ? "bg-green-950/60 text-green-400 border border-green-700/40"
              : "bg-yellow-950/60 text-yellow-400 border border-yellow-700/40"
          }`}
        >
          {isPaid ? "Deposit Paid" : "Unpaid"}
        </span>
      </div>

      <div className="mt-4 grid sm:grid-cols-2 gap-x-6 gap-y-3 text-sm">
        <InfoLine icon={<PhoneIcon className="w-4 h-4" />} label="Phone">
          <a href={`tel:${booking.phone}`} className="text-red-400 hover:text-red-300">
            {booking.phone}
          </a>
        </InfoLine>
        <InfoLine icon={<MailIcon className="w-4 h-4" />} label="Email">
          <a
            href={`mailto:${booking.email}`}
            className="text-red-400 hover:text-red-300 break-all"
          >
            {booking.email}
          </a>
        </InfoLine>
        <InfoLine icon={<ClipboardList className="w-4 h-4" />} label="Service">
          {booking.service_type}
        </InfoLine>
        <InfoLine icon={<Car className="w-4 h-4" />} label="Vehicle">
          {vehicle}
        </InfoLine>
        <InfoLine icon={<Calendar className="w-4 h-4" />} label="When">
          {when}
        </InfoLine>
        <InfoLine icon={<MapPin className="w-4 h-4" />} label="Where">
          {booking.location || "—"}
        </InfoLine>
        <InfoLine icon={<DollarSign className="w-4 h-4" />} label="Deposit">
          ${Number(booking.deposit_amount || 0).toFixed(2)} {booking.currency?.toUpperCase()}
        </InfoLine>
      </div>

      {booking.notes && (
        <div className="mt-4 pt-3 border-t border-zinc-800">
          <div className="text-xs text-zinc-500 font-bold uppercase tracking-widest mb-1.5">
            Notes
          </div>
          <div className="text-zinc-300 text-sm whitespace-pre-wrap break-words">
            {booking.notes}
          </div>
        </div>
      )}
    </div>
  );
};

const InfoLine = ({ icon, label, children }) => (
  <div className="flex items-start gap-2.5">
    <div className="w-7 h-7 shrink-0 rounded-md bg-red-950/40 border border-red-900/30 text-red-500 flex items-center justify-center mt-0.5">
      {icon}
    </div>
    <div className="min-w-0">
      <div className="text-[10px] text-zinc-500 font-bold uppercase tracking-widest">
        {label}
      </div>
      <div className="text-zinc-200 mt-0.5 break-words">{children}</div>
    </div>
  </div>
);

export default Admin;
